import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import withRouter from "components/Common/withRouter";
import { toast } from "react-toastify";
import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Input,
  Row,
} from "reactstrap";
import CommonTableForCandidate from "components/Common/CandidateTableContainer";
import LegacyFilterToolbar, {
  LEGACY_FILTER_INPUT_CLASS,
} from "components/Common/LegacyFilterToolbar";
import { fetchCandidateCsvMetadata, submitCandidateCsvFilterData } from "model/CandidateModel";
import { DEFAULT_PAGE_SIZE, mapApiMetadataToTableColumnDefinition } from "helpers/GeneralHelper";
import { AsyncClientSelect } from "components/Common/AsyncSearchSelects";
import {
  applyClientAsyncFilterOptionToFormState,
  buildClientAsyncFilterInitialOption,
  getClientAsyncFilterLabelFieldKey,
} from "helpers/metadataFilterHelpers";
import Loaders from "components/Common/Loaders";
import { useCommonFilterOptions } from "libs/context/CommonFilterOptionsContext";
import { useCandidateTableSettingsPageSize } from "hooks/useSettingsReadyTablePageSize";


/** Status codes shown in UI (metadata can override labels). */
const CSV_STATUS_OPTIONS = [
  { value: "", label: "Status" },
  { value: "dcie", label: "dcie" },
  { value: "cnf", label: "cnf" },
  { value: "enf", label: "enf" },
  { value: "apie", label: "apie" },
  { value: "in_sent", label: "in_sent" },
  { value: "completed", label: "completed" },
  { value: "send", label: "send" },
];

const API_STATUS_OPTIONS = [
  { value: "", label: "API Status" },
  { value: "completed", label: "completed" },
  { value: "failed", label: "failed" },
  { value: "Sent", label: "Sent" },
  { value: "On", label: "On" },
];

const strOrNull = (v) => {
  if (v === null || v === undefined) return null;
  const s = String(v).trim();
  return s === "" ? null : s;
};

const resolveImageUrl = (row = {}) => {
  const raw =
    row?.link
  const s = String(raw || "").trim();
  if (!s) return "";
  if (/^https?:\/\//i.test(s)) return s;
  const base = process.env.REACT_APP_LINK_URL || process.env.REACT_APP_ASSETS_URL || "";
  return `${base}${s.startsWith("/") ? s : `/${s}`}`;
};

const FadvCsvLog = () => {
  const { fetchMetadataSchema } = useCommonFilterOptions();
  const { pageSize: settingsPageSize, settingsReady } =
    useCandidateTableSettingsPageSize("fadv-csv-log");
  const initialFilterFetchedRef = useRef(false);
  const [metadataBootstrapDone, setMetadataBootstrapDone] = useState(false);
  const [filterForm, setFilterForm] = useState({
    search: "",
    clientId: "",
    [getClientAsyncFilterLabelFieldKey()]: "",
    status: "",
    apiStatus: "",
    fromDate: "",
    toDate: "",
  });
  const [loading, setLoading] = useState(true);
  const [tableMetaData, setTableMetaData] = useState([]);
  const [rows, setRows] = useState([]);
  const [requestPayload, setRequestPayload] = useState({
    pageNumber: 1,
    pageSize: DEFAULT_PAGE_SIZE,
  });
  const [paginationMeta, setPaginationMeta] = useState({
    pageNumber: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    totalPages: 0,
    totalRecords: 0,
  });

  const getList = async (payloadOverrides = {}) => {
    setLoading(true);
    try {
      const payload = {
        ...requestPayload,
        ...payloadOverrides,
      };
      const response = await submitCandidateCsvFilterData(payload);
      const responseData = response?.data || {};
      const list = responseData?.records ?? responseData?.data ?? responseData ?? [];
      const data = Array.isArray(list) ? list : [];
      setRows(data.map((item) => ({ ...item, id: item?.id || item?._id || "" })));
      setPaginationMeta({
        pageNumber: Number(responseData?.pageNumber) || Number(payload?.pageNumber) || 1,
        pageSize: Number(responseData?.pageSize) || Number(payload?.pageSize) || DEFAULT_PAGE_SIZE,
        totalPages: Number(responseData?.totalPages) || 0,
        totalRecords: Number(responseData?.totalRecords) || data.length,
      });
      setRequestPayload(payload);
    } catch (e) {
      toast.error(e?.message || "Unable to load FADV CSV log");
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const bootstrap = async () => {
      setLoading(true);
      try {
        const metaRes = await fetchMetadataSchema("metadata:candidateCsv", () =>
          fetchCandidateCsvMetadata({})
        );
        const meta = metaRes?.data ?? metaRes ?? {};
        const tableColumns = meta?.table?.columns?.length
          ? meta.table.columns.map((col) => mapApiMetadataToTableColumnDefinition(col))
          : [];
        setTableMetaData(tableColumns);
      } catch (e) {
        toast.error(e?.message || "Unable to load FADV CSV log metadata");
        setTableMetaData([]);
      }

      setMetadataBootstrapDone(true);
    };
    bootstrap();
  }, []);

  /** 1) settings page size ready → 2) call filter API once */
  useEffect(() => {
    if (!settingsReady || !metadataBootstrapDone || initialFilterFetchedRef.current) return;
    initialFilterFetchedRef.current = true;
    const pageSize = Number(settingsPageSize) || DEFAULT_PAGE_SIZE;
    setRequestPayload((previous) => ({ ...previous, pageSize }));
    setPaginationMeta((previous) => ({ ...previous, pageSize }));
    void getList({ pageNumber: 1, pageSize });
  }, [settingsReady, settingsPageSize, metadataBootstrapDone]);

  const handleFilterFieldChange = (e) => {
    const { name, value } = e.target;
    setFilterForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleClientSelect = (opt) => {
    setFilterForm((prev) => applyClientAsyncFilterOptionToFormState(prev, opt));
  };

  const buildSearchPayload = (form = filterForm) => {
    const f = form || {};
    return {
      pageNumber: 1,
      pageSize: requestPayload?.pageSize || DEFAULT_PAGE_SIZE,
      nameContains: strOrNull(f.search),
      search: strOrNull(f.search),
      name: strOrNull(f.search),
      clientId: strOrNull(f.clientId),
      status: strOrNull(f.status),
      csvStatus: strOrNull(f.status),
      apiStatus: strOrNull(f.apiStatus),
      fromDate: strOrNull(f.fromDate),
      toDate: strOrNull(f.toDate),
    };
  };

  const handleSearch = async () => {
    await getList(buildSearchPayload());
  };

  const handlePageChange = async (page) => {
    await getList({ pageNumber: page });
  };

  const handleRowsPerPageChange = async (rowsPerPage) => {
    await getList({ pageNumber: 1, pageSize: rowsPerPage });
  };

  const handleViewImage = (row) => {
    const url = resolveImageUrl(row);
    if (!url) {
      toast.error("No image available for this row");
      return;
    }
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const filteredData = useMemo(() => rows, [rows]);

  return (
    <div className="page-content fadv-csv-log-page">
      <Container fluid>

        <Card>
          <CardBody>
            {/* <div className="mb-3 h5 fw-bold">FADV CSV Log</div> */}
            <LegacyFilterToolbar>
            <Row className="metadata-filter-toolbar__row g-2 g-md-3 align-items-end">
              <Col xs="12" md="2" className="metadata-filter-field metadata-filter-field--text">
                <span className="manage-user-filter-label">Search</span>
                <div className="metadata-filter-field__control">
                <Input
                  type="text"
                  name="search"
                  placeholder="Search by Name"
                  className={LEGACY_FILTER_INPUT_CLASS}
                  value={filterForm.search}
                  onChange={handleFilterFieldChange}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleSearch();
                  }}
                />
                </div>
              </Col>

              <Col xs="12" md="2" className="metadata-filter-field metadata-filter-field--autocomplete">
                <span className="manage-user-filter-label">Client</span>
                <div className="metadata-filter-field__control">
                <AsyncClientSelect
                  key={`fadv-csv-log-client-${filterForm.clientId}`}
                  pageName="FadvLog"
                  value={filterForm.clientId}
                  initialOption={buildClientAsyncFilterInitialOption(
                    filterForm.clientId,
                    filterForm[getClientAsyncFilterLabelFieldKey()]
                  )}
                  onChange={handleClientSelect}
                  placeholder="Select Client"
                />
                </div>
              </Col>

              <Col xs="12" md="2" className="metadata-filter-field metadata-filter-field--dropdown">
                <span className="manage-user-filter-label">Status</span>
                <div className="metadata-filter-field__control">
                <Input
                  type="select"
                  name="status"
                  value={filterForm.status}
                  onChange={handleFilterFieldChange}
                  className={LEGACY_FILTER_INPUT_CLASS}
                >
                  {CSV_STATUS_OPTIONS.map((o) => (
                    <option key={`st-${o.value}-${o.label}`} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </Input>
                </div>
              </Col>

              <Col xs="12" md="2" className="metadata-filter-field metadata-filter-field--dropdown">
                <span className="manage-user-filter-label">API Status</span>
                <div className="metadata-filter-field__control">
                <Input
                  type="select"
                  name="apiStatus"
                  value={filterForm.apiStatus}
                  onChange={handleFilterFieldChange}
                  className={LEGACY_FILTER_INPUT_CLASS}
                >
                  {API_STATUS_OPTIONS.map((o) => (
                    <option key={`api-${o.value}-${o.label}`} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </Input>
                </div>
              </Col>

              <Col xs="12" md="2" className="metadata-filter-field metadata-filter-field--date">
                <span className="manage-user-filter-label">From date</span>
                <div className="metadata-filter-field__control">
                <Input
                  type="date"
                  name="fromDate"
                  value={filterForm.fromDate}
                  onChange={handleFilterFieldChange}
                  className={LEGACY_FILTER_INPUT_CLASS}
                />
                </div>
              </Col>

              <Col xs="12" md="2" className="metadata-filter-field metadata-filter-field--date">
                <span className="manage-user-filter-label">To date</span>
                <div className="metadata-filter-field__control">
                <Input
                  type="date"
                  name="toDate"
                  value={filterForm.toDate}
                  onChange={handleFilterFieldChange}
                  className={LEGACY_FILTER_INPUT_CLASS}
                />
                </div>
              </Col>

              <Col xs="12" md="2" className="metadata-filter-field metadata-filter-field--actions">
                <span className="manage-user-filter-label" aria-hidden="true">&nbsp;</span>
                <div className="metadata-filter-toolbar__actions">
                <Button color="primary" className="w-100 manage-user-search-btn" onClick={handleSearch}>
                  <i className="fa fa-search me-1" />
                  Search
                </Button>
                </div>
              </Col>
            </Row>
            </LegacyFilterToolbar>

            <p className="fadv-csv-log-note mb-4">
              <i className="mdi mdi-information-outline fadv-csv-log-note__icon" aria-hidden />
              <span className="fadv-csv-log-note__content">
                Note: &quot;dcie&quot; = drug csv insert error
                <span className="fadv-csv-log-note__dot" aria-hidden />
                &quot;cnf&quot; = candidate not found
                <span className="fadv-csv-log-note__dot" aria-hidden />
                &quot;enf&quot; = email not found
                <span className="fadv-csv-log-note__dot" aria-hidden />
                &quot;apie&quot; = api error
              </span>
            </p>
            {loading ? (
              <Loaders />
            ) : tableMetaData?.length ? (
              <CommonTableForCandidate
                columns={tableMetaData}
                data={filteredData}
                isActionRequired
                onViewImage={handleViewImage}
                pageName="fadv-csv-log"
                isPagination={true}
                currentPage={paginationMeta?.pageNumber}
                rowsPerPage={paginationMeta?.pageSize}
                totalPages={paginationMeta?.totalPages}
                totalRecords={paginationMeta?.totalRecords}
                onPageChange={handlePageChange}
                onPageSizeChange={handleRowsPerPageChange}
              />
            ) : (
              <div className="text-muted text-center py-5">Unable to load table columns.</div>
            )}
          </CardBody>
        </Card>
      </Container>
    </div>
  );
};

FadvCsvLog.propTypes = {
  orders: PropTypes.array,
  onGetOrders: PropTypes.func,
};

export default withRouter(FadvCsvLog);
